import { openDB, type DBSchema } from 'idb';

export type PendingStatus = 'PENDING' | 'FAILED' | 'CONFLICT';

export interface PendingTimeEntry {
  localId: string;
  userId: string;
  projectId: string;
  taskId: string;
  date: string;
  duration: string;
  note: string;
  status: PendingStatus;
  error?: string;
  duplicateId?: string;
  createdAt: number;
}

interface JaniOfflineDB extends DBSchema {
  pendingEntries: {
    key: string;
    value: PendingTimeEntry;
  };
  referenceCache: {
    key: string;
    value: { key: string; data: unknown; timestamp: number };
  };
}

const DB_NAME = 'JaniOfflineDB';
const DB_VERSION = 1;

async function getDB() {
  if (typeof window === 'undefined') throw new Error('IndexedDB is only available in browser');
  return openDB<JaniOfflineDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains('pendingEntries')) {
        db.createObjectStore('pendingEntries', { keyPath: 'localId' });
      }
      if (!db.objectStoreNames.contains('referenceCache')) {
        db.createObjectStore('referenceCache', { keyPath: 'key' });
      }
    },
  });
}

export async function addPendingEntry(entry: PendingTimeEntry): Promise<void> {
  const db = await getDB();
  await db.put('pendingEntries', entry);
}

export async function getPendingEntries(): Promise<PendingTimeEntry[]> {
  const db = await getDB();
  return db.getAll('pendingEntries');
}

export async function removePendingEntry(localId: string): Promise<void> {
  const db = await getDB();
  await db.delete('pendingEntries', localId);
}

export async function clearPendingEntries(): Promise<void> {
  const db = await getDB();
  await db.clear('pendingEntries');
}

export async function updatePendingEntry(
  localId: string,
  updates: Partial<Pick<PendingTimeEntry, 'status' | 'error' | 'duplicateId'>>
): Promise<void> {
  const db = await getDB();
  const entry = await db.get('pendingEntries', localId);
  if (!entry) throw new Error('Entry not found');
  await db.put('pendingEntries', { ...entry, ...updates });
}

export async function setReferenceCache(key: string, data: unknown): Promise<void> {
  const db = await getDB();
  await db.put('referenceCache', { key, data, timestamp: Date.now() });
}

export async function getReferenceCache(key: string): Promise<unknown | undefined> {
  const db = await getDB();
  const result = await db.get('referenceCache', key);
  return result?.data;
}
